/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    CircularProgress,
    IconButton,
    InputAdornment,
    Chip,
    Tabs,
    Tab,
    Box,
    Button,
} from "@mui/material";
import { ContentCopy, Preview, EditNote } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import PaymentScheduleList from "components/PaymentScheduleList";
import { useAuth } from "auth/Auth";
import { getApiErrorMessage, isEmailProviderAuthFailure } from "utils/apiErrors";
import {
  buildPaymentLinkEmail,
  buildPaymentLinkEmailHtml,
  buildPaymentSummaryContext,
  formatPaymentLabel,
} from "utils/paymentLinkEmailTemplate";

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

const PAYMENT_TYPE_CHIP = {
  full: { label: "Full payment", color: "info" },
  deposit_balance: { label: "Deposit + balance due later", color: "warning" },
  installment: { label: "Deposit + installments", color: "warning" },
};

const INBOX_SETUP_MESSAGE =
  "Connect your inbox first (Gmail or Outlook), then send the payment email. Stripe is ready — email still needs Inbox setup.";

export default function SendPaymentLinkModal({
  open,
  onClose,
  invoiceId,
  defaultEmail,
  onSent,
  updateSnackbar,
  title = "Send payment link",
}) {
  const { axiosInstance } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [inboxReady, setInboxReady] = useState(true);
  const [linkData, setLinkData] = useState(null);
  const [company, setCompany] = useState({});
  const [emailTab, setEmailTab] = useState(0);
  const [email, setEmail] = useState({ to: "", subject: "", content: "" });
  const [personalNote, setPersonalNote] = useState("");

  useEffect(() => {
    if (!open || !invoiceId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      setInboxReady(true);
      setEmailTab(0);
      setPersonalNote("");
      try {
        const companyPromise = axiosInstance.get("/company/info").catch(() => ({ data: {} }));
        const inboxPromise = axiosInstance.get("/email-settings").catch(() => ({ data: null }));

        const linkRes = await axiosInstance.get(`/payment-gateways/invoices/${invoiceId}/payment-link`);
        if (cancelled) return;

        // fetch order details if the payment link references one
        const orderData = linkRes.data?.travel_order_id
            ? await axiosInstance
                .get(`travel-orders/${linkRes.data.travel_order_id}/detail`)
                .then((res) => res.data || {})
                .catch((orderErr) => {
                  // non-fatal: payment link should still work even if this fails
                  console.error("Failed to load travel order details", orderErr);
                  return {};
                })
            : {};
        if (cancelled) return;

        const orderRecord = orderData; // handles wrapped or flat response

        const [companyRes, inboxRes] = await Promise.all([companyPromise, inboxPromise]);
        if (cancelled) return;

        const hasInbox = Boolean(inboxRes?.data?.email_provider);
        setInboxReady(hasInbox);
        if (!hasInbox) {
          setError(INBOX_SETUP_MESSAGE);
        }

        // order/trip data first, invoice/payment-link data last so it always wins on true overlaps
        // (protects amount/currency/payment_url/schedule from being clobbered by order/trip response)
        // Travel: keep package currency from the order (INR etc.) — do not let a wrong
        // company/default currency on the payment-link response relabel the amount as USD.
        const orderCurrency = (
          orderRecord?.payment?.currency ||
          orderRecord?.trip?.currency ||
          ""
        ).trim();
        const mergedLinkData = {
          ...orderData,
          ...linkRes.data,
          currency: orderCurrency || linkRes.data?.currency || "",
          order_number: orderRecord?.order_id,
          // adjust these field names once you confirm the actual /trips/{trip_id} response shape
          trip_departure_date: orderRecord?.departure_date,
          trip_package_name: orderRecord?.trip?.package_name || orderRecord?.trip?.trip_name,
          travellers: orderRecord?.travellers,
        };

        setLinkData(mergedLinkData);
        setCompany(companyRes.data || {});
        const emailDefaults = buildPaymentLinkEmail(mergedLinkData, companyRes.data || {}, {
          origin: typeof window !== "undefined" ? window.location.origin : undefined,
        });
        if (defaultEmail?.trim()) {
          emailDefaults.to = defaultEmail.trim();
        }
        setEmail(emailDefaults);
      } catch (err) {
        if (!cancelled) {
          setLinkData(null);
          const detail = err?.response?.data?.detail;
          const detailText = typeof detail === "string" ? detail : "";
          setError(
            detailText ||
              err?.message ||
              "Unable to load payment link. Save the booking and try again."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, invoiceId, defaultEmail, axiosInstance]);

  useEffect(() => {
    if (!linkData || linkData.already_paid) return;
    const built = buildPaymentLinkEmail(linkData, company, {
      personalNote,
      origin: typeof window !== "undefined" ? window.location.origin : undefined,
    });
    setEmail((prev) => ({
      ...prev,
      subject: built.subject,
      content: built.content,
    }));
  }, [linkData, company, personalNote]);

  // Payment-type-aware summary (total, payable-now, future installments, note)
  const summary = useMemo(() => {
    if (!linkData || linkData.already_paid) return null;
    return buildPaymentSummaryContext(linkData);
  }, [linkData]);

  const previewHtml = useMemo(() => {
    if (!linkData || linkData.already_paid) return "";
    return buildPaymentLinkEmailHtml({
      customerName: linkData.customer_name || "",
      paymentLabel: formatPaymentLabel(linkData),
      amountFormatted: `${linkData.currency || ""} ${Number(linkData.amount || 0).toFixed(2)}`.trim(),
      invoiceNumber: linkData.invoice_number || "",
      paymentUrl: linkData.payment_url || "",
      companyName: company.company_name || company.name || "PearlCRM",
      logoUrl: "",
      gateway: linkData.gateway || "stripe",
      paymentType: linkData.order_payment_type || "",
      personalNote,
      summary,
    });
  }, [linkData, company, personalNote, summary]);

  const copyLink = async () => {
    if (!linkData?.payment_url) return;
    try {
      updateSnackbar({
        open: true,
        message: "Successfully copied to Clipboard",
        severity: 'success'
      });
      await navigator.clipboard.writeText(linkData.payment_url);
    } catch {
      setError("Could not copy payment link.");
    }
  };

  const goToInboxSetup = () => {
    onClose?.();
    navigate("/inbox");
  };

  const handleSend = async () => {
    if (!linkData?.payment_url) {
      setError("Payment link is not ready. Close and try again.");
      return;
    }
    if (!email.to?.trim()) {
      setError("Recipient email is required.");
      return;
    }
    if (!inboxReady) {
      setError(INBOX_SETUP_MESSAGE);
      return;
    }
    setSending(true);
    setError("");
    try {
      const configRes = await axiosInstance.get("/email-settings");
      if (!configRes.data?.email_provider) {
        setInboxReady(false);
        throw new Error(INBOX_SETUP_MESSAGE);
      }
      await axiosInstance.post("/emails/send", {
        to: email.to,
        subject: email.subject,
        content: email.content,
        attachments: [],
        metadata: { parent_type: "invoice", parent_id: invoiceId, payment_link: true },
      });
      onSent?.();
      onClose?.();
    } catch (err) {
      const detailText = getApiErrorMessage(err, "");
      const lower = `${detailText} ${err?.message || ""}`.toLowerCase();
      // Only treat inbox/email-settings failures as "connect inbox" — not every 404
      // (payment-link / pending-payment 404s must stay visible as real errors).
      const isInboxError =
        isEmailProviderAuthFailure(err) ||
        lower.includes("not configured") ||
        lower.includes("inbox") ||
        lower.includes("email settings") ||
        lower.includes("email is not configured") ||
        lower.includes("email connection expired") ||
        (err?.response?.status === 404 && lower.includes("email"));
      if (isInboxError) {
        setInboxReady(false);
        setError(
          isEmailProviderAuthFailure(err)
            ? detailText ||
              "Your connected email inbox expired. Reconnect Gmail or Outlook in Inbox, then try again."
            : INBOX_SETUP_MESSAGE
        );
      } else {
        setError(detailText || err?.message || "Failed to send email. Please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  const typeChip = summary ? PAYMENT_TYPE_CHIP[summary.paymentType] : null;

  return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          {error && (
              <Alert
                severity={inboxReady ? "error" : "warning"}
                sx={{ mb: 2 }}
                onClose={() => setError("")}
                action={
                  !inboxReady ? (
                    <Button color="inherit" size="small" onClick={goToInboxSetup}>
                      Set up Inbox
                    </Button>
                  ) : null
                }
              >
                {typeof error === "string" ? error : JSON.stringify(error)}
              </Alert>
          )}

          {loading ? (
              <MDBox display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </MDBox>
          ) : linkData?.already_paid ? (
              <Alert severity="success">This invoice is already paid. No payment link is needed.</Alert>
          ) : !linkData ? (
              <Alert severity="info">
                Fix the issue above, then close this dialog and open Send payment link again.
              </Alert>
          ) : (
              <>

                {/* SEND PAYMENT LINK MODAL - HEADER */}

                <MDBox display="flex" flexWrap="wrap" gap={1} mb={2}>
                  {typeChip && <Chip label={typeChip.label} color={typeChip.color} size="small" />}
                  <Chip
                      label={`Payable now: ${linkData?.currency || ""} ${Number(linkData?.amount || 0).toFixed(2)}`}
                      size="small"
                  />
                  {summary?.totalAmountFormatted && (
                      <Chip
                        label={`${summary.isTravel ? "Order total" : "Invoice amount"}: ${summary.totalAmountFormatted}`}
                        size="small"
                        variant="outlined"
                      />
                  )}
                  <Chip label={linkData?.gateway === "razorpay" ? "Razorpay" : "Stripe"} size="small" />
                </MDBox>

                {/* SEND PAYMENT LINK MODAL - INFO */}

                {summary?.contextNote && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {summary.contextNote}
                    </Alert>
                )}

                {/* SEND PAYMENT LINK MODAL - COPY PAYMENT LINK BUTTON */}

                <TextField
                    fullWidth
                    label="Payment link"
                    value={linkData?.payment_url || ""}
                    margin="normal"
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={copyLink} edge="end">
                              <ContentCopy />
                            </IconButton>
                          </InputAdornment>
                      ),
                    }}
                />

                {/* SEND PAYMENT LINK MODAL - PAYMENT SCHEDULE */}

                {linkData?.schedule?.length > 0 && (
                    <MDBox mt={2} mb={2}>
                      <MDTypography variant="subtitle2" fontWeight="medium" mb={1}>
                        Payment schedule
                      </MDTypography>
                      <PaymentScheduleList
                          rows={linkData.schedule}
                          highlightInvoiceId={linkData.invoice_id}
                          showInvoiceNumber={linkData.schedule.length > 1}
                      />
                    </MDBox>
                )}

                {/* SEND PAYMENT LINK MODAL - EMAIL TO */}

                <TextField
                    fullWidth
                    label="To"
                    value={email.to}
                    onChange={(e) => setEmail((prev) => ({ ...prev, to: e.target.value }))}
                    margin="normal"
                />

                {/* SEND PAYMENT LINK MODAL - EMAIL SUBJECT */}

                <TextField
                    fullWidth
                    label="Subject"
                    value={email.subject}
                    onChange={(e) => setEmail((prev) => ({ ...prev, subject: e.target.value }))}
                    margin="normal"
                />

                <MDBox mt={2}>
                  <Tabs
                      value={emailTab}
                      onChange={(_, v) => setEmailTab(v)}
                  >
                    <Tab icon={<Preview fontSize="small" />} iconPosition="start" label="Preview" />
                    <Tab icon={<EditNote fontSize="small" />} iconPosition="start" label="Customize" />
                  </Tabs>

                  {/* SEND PAYMENT LINK MODAL - PREVIEW TAB */}

                  <TabPanel value={emailTab} index={0}>
                    <MDTypography variant="caption" color="text" display="block" mb={1}>
                      This is how the email will appear to your customer.
                    </MDTypography>
                    <MDBox
                        sx={{
                          bgcolor: "#EEF3F9",
                          borderRadius: "12px",
                          border: "1px solid #E6E8F0",
                          p: 1,
                          maxHeight: 520,
                          overflow: "hidden",
                        }}
                    >
                      <iframe
                          title="Payment email preview"
                          srcDoc={previewHtml}
                          style={{
                            width: "100%",
                            minHeight: 560,
                            border: "none",
                            borderRadius: 8,
                            background: "#EEF3F9",
                          }}
                      />
                    </MDBox>
                  </TabPanel>

                  {/* SEND PAYMENT LINK MODAL - CUSTOMIZE TAB */}

                  <TabPanel value={emailTab} index={1}>
                    <TextField
                        fullWidth
                        label="Personal message (optional)"
                        value={personalNote}
                        onChange={(e) => setPersonalNote(e.target.value)}
                        multiline
                        minRows={3}
                        margin="normal"
                        placeholder="Add a short note for your customer — it appears above the Pay button."
                        helperText="The branded PearlCRM layout, order summary, and pay button are added automatically."
                    />
                  </TabPanel>
                </MDBox>
              </>
          )}
        </DialogContent>

        {/* SEND PAYMENT LINK MODAL - ACTION BUTTONS */}

        <DialogActions>
          <MDButton color="secondary" onClick={onClose}>
            Close
          </MDButton>
          {!linkData?.already_paid && !loading && linkData?.payment_url && (
              <MDButton
                variant="gradient"
                color="info"
                onClick={handleSend}
                disabled={sending || !inboxReady}
              >
                {sending ? <CircularProgress size={20} color="inherit" /> : "Send email"}
              </MDButton>
          )}
        </DialogActions>
      </Dialog>
  );
}